import { createAsyncThunk } from '@reduxjs/toolkit';
import { ChartData } from '../../types';
import {
  getDatabaseStats,
  getFilters,
  updateFilters as updateFiltersAPI,
  getDatabaseFilters,
  updateDatabaseFilters,
  syncFiltersFromDatabase,
  screenStocks,
  stopScreening as stopScreeningAPI,
  getResults,
  getChartData,
  analyzeStocks,
  compareStocks,
  analyzeStockColor,
  analyzeStockWithImage,
  analyzeStockStructured,
  pollScreeningStatus,
  invalidateResultsCache,
  getFavorites,
  addFavorite as addFavoriteAPI,
  updateFavorite as updateFavoriteAPI,
  deleteFavorite as deleteFavoriteAPI,
  forceDataUpdate,
  createRequestCacheKey,
  cancelRequest,
  cancelAllRequests,
  analyzeTickerWithGrok,
  analyzeBatchWithGrok,
  analyzeStockWithPerplexity,
  analyzeStockWithPerplexityChart,
} from '../../services/api';
import {
  areScreeningStatusesEqual,
  areScreeningResultsEqual,
  areDatabaseStatsEqual,
  SmartThrottle,
  performanceMonitor
} from '../../utils/stateUtils';
import {
  setDatabaseStats
} from '../slices/databaseSlice';
import {
  setFilters,
  resetFilters
} from '../slices/filtersSlice';
import {
  setScreeningStatus,
  setScreeningResults,
  setLastUpdated
} from '../slices/screeningSlice';
import {
  setStocks
} from '../slices/stocksSlice';
import {
  setChartData,
  setChartLoading,
  setChartError,
  clearChart
} from '../slices/chartSlice';
import {
  setAIAnalysisData,
  setColorAnalysisData,
  setImageAnalysisData,
  setAIAnalysisLoading,
  setAIAnalysisError
} from '../slices/aiAnalysisSlice';
import {
  showGlobalMessage
} from '../slices/globalMessageSlice';
import {
  setFavorites,
  addFavorite,
  updateFavorite,
  removeFavorite,
  setLoading,
  setError
} from '../slices/favoritesSlice';
import {
  setSentimentData,
  setBatchSentimentData,
  setSentimentLoading,
  setBatchSentimentLoading,
  setSentimentError,
} from '../slices/grokSentimentSlice';
import {
  setAnalysisData,
  setAnalysisLoading,
  setAnalysisError,
} from '../slices/perplexityAnalysisSlice';

// Global polling throttle instances
const screeningPollingThrottle = new SmartThrottle(1000, 5000);
const databaseStatsThrottle = new SmartThrottle(2000, 10000);
let screeningStartInFlight = false;


const isRequestCancellation = (error: any): boolean => {
  const message = (error?.message || '').toString().toLowerCase();
  return (
    error?.type === 'AbortError' ||
    error?.name === 'AbortError' ||
    error?.name === 'CanceledError' ||
    error?.code === 'ERR_CANCELED' ||
    message.includes('aborted') ||
    message.includes('canceled') ||
    message.includes('cancelled')
  );
};

// Utility functions for request cancellation
export const cancelChartRequests = (ticker?: string) => {
  if (ticker) {
    // Cancel both DAILY and WEEKLY timeframe requests for the ticker
    (['DAILY', 'WEEKLY'] as const).forEach((timeframe) => {
      const cacheKey = createRequestCacheKey(
        'GET',
        `/chart_data/${ticker}?timeframe=${encodeURIComponent(timeframe)}`
      );
      cancelRequest(cacheKey);
    });

    // Backward-compat: cancel legacy key without timeframe, if any callers still use it
    cancelRequest(createRequestCacheKey('GET', `/chart_data/${ticker}`));
  } else {
    // Cancel all chart requests
    cancelAllRequests();
  }
};

export const cancelAnalysisRequests = () => {
  // Cancel any pending AI analysis requests
  const analysisKeys = [
    'analyze_multiple',
    'compare',
    'analyze_multiple_color',
    'quick_insight',
    'analyze_structured'
  ];

  analysisKeys.forEach(endpoint => {
    const cacheKey = createRequestCacheKey('POST', `/perplexity/${endpoint}`);
    cancelRequest(cacheKey);
  });
};

// Optimized database stats with diff detection
export const fetchDatabaseStats = createAsyncThunk(
  'database/fetchStats',
  async (force: boolean = false, { dispatch, getState }) => {
    try {
      // Check throttling unless forced
      if (!force && !databaseStatsThrottle.shouldUpdate()) {
        return;
      }

      const response = await getDatabaseStats();
      const newStats = response.database_stats;

      // Get current state for comparison
      const currentState = getState() as any;
      const currentStats = currentState.database;

      // Only update if stats actually changed
      if (!areDatabaseStatsEqual(currentStats, newStats)) {
        dispatch(setDatabaseStats(newStats));
        console.log('📊 Database stats updated');
      }

      return newStats;
    } catch (error) {
      console.error('Error fetching database stats:', error);
      throw error;
    }
  }
);

// Fetch previous screening results
export const fetchPreviousResults = createAsyncThunk(
  'screening/fetchPreviousResults',
  async (_, { dispatch }) => {
    try {
      dispatch(setScreeningStatus({ status: 'loading' }));
      const raw = await getResults(true);  // Skip cache to get fresh results
      const results = Array.isArray(raw) ? raw : [];
      if (results.length > 0) {
        dispatch(setScreeningResults(results));
        dispatch(setStocks(results));
        dispatch(setScreeningStatus({
          status: 'completed',
          total_stocks: results.length,
          passing_count: results.length,
          processed_count: results.length
        }));
        dispatch(setLastUpdated(new Date().toISOString()));
        console.log(`📊 Restored ${results.length} previous screening results`);
        dispatch(showGlobalMessage({
          message: `📊 Restored ${results.length} previous screening results from server`,
          type: 'success'
        }));
                             } else {
           dispatch(setScreeningStatus({ status: 'idle' }));
         }
      return results;
    } catch (error) {
      // Startup request may be cancelled during rapid tab/state transitions.
      // Avoid noisy error logs / error state for expected cancellation paths.
      if (isRequestCancellation(error)) {
        dispatch(setScreeningStatus({ status: 'idle' }));
        return [];
      }
      // A1: a real failure (e.g. /results 503) must NOT masquerade as an empty
      // "No Stocks Found" idle state - the backend now returns 503 JSON instead
      // of an HTML 500, so surface it as an error and let the user know the
      // server, not their filters, is the problem.
      console.error('Error fetching previous results:', error);
      dispatch(setScreeningStatus({ status: 'error' }));
      // Don't throw error - this is optional on startup
      return [];
    }
  }
);

// Filters
export const fetchFilters = createAsyncThunk(
  'filters/fetch',
  async (_, { dispatch }) => {
    try {
      const filters = await getFilters();
      dispatch(setFilters(filters));
      return filters;
    } catch (error) {
      console.error('Error fetching filters:', error);
      throw error;
    }
  }
);

export const updateFiltersThunk = createAsyncThunk(
  'filters/update',
  async (filters: any, { dispatch }) => {
    try {
      await updateFiltersAPI(filters);
      dispatch(setFilters(filters));
      dispatch(showGlobalMessage({
        message: '✅ Filters updated successfully!',
        type: 'success'
      }));
    } catch (error: any) {
      console.error('Error updating filters:', error);
      dispatch(showGlobalMessage({
        message: `❌ ${error.message || 'Error updating filters'}`,
        type: 'danger'
      }));
      throw error;
    }
  }
);

// Database-backed filter thunks (single source of truth)
export const fetchDatabaseFilters = createAsyncThunk(
  'filters/fetchDatabase',
  async (_, { dispatch }) => {
    try {
      const filters = await getDatabaseFilters();
      // Only update Redux if we have stored filters (not null)
      if (filters !== null) {
        dispatch(setFilters(filters));
        console.log('📊 Loaded filters from database');
        return filters;
      } else {
        // New user (no saved config) MUST get clean defaults - never inherit the
        // previous user's in-memory Redux filters left over from before logout.
        dispatch(resetFilters());
        console.log('📊 No stored filters - reset to defaults for this user');
        return null;
      }
    } catch (error) {
      // Do NOT fall back to GET /filters: that endpoint serves the shared global
      // screening-engine config (last writer's values) and would leak another
      // user's filters into this session. Do NOT reset to defaults either - a
      // transient DB blip would then silently replace a returning user's loaded
      // config with defaults, which they could unknowingly save over their real
      // one. Cross-user leakage is already prevented by the logout-time reset and
      // by Redux reinitialising on page reload, so just keep current state and log.
      console.error('Error fetching database filters (keeping current filter state):', error);
      return null;
    }
  }
);

export const updateDatabaseFiltersThunk = createAsyncThunk(
  'filters/updateDatabase',
  async (filters: any, { dispatch }) => {
    try {
      await updateDatabaseFilters(filters);
      // BUG-CP-PERSIST FIX: After saving, reload from database to get the actual persisted values
      // This ensures we have the correct merged config that was actually saved
      const savedFilters = await getDatabaseFilters();
      dispatch(setFilters(savedFilters));
      dispatch(showGlobalMessage({
        message: '✅ Filters updated in database successfully!',
        type: 'success'
      }));
      console.log('📊 Updated filters in database');
    } catch (error: any) {
      console.error('Error updating database filters:', error);
      dispatch(showGlobalMessage({
        message: `❌ ${error.message || 'Error updating filters in database'}`,
        type: 'danger'
      }));
      throw error;
    }
  }
);

export const syncFiltersFromDatabaseThunk = createAsyncThunk(
  'filters/syncFromDatabase',
  async (_, { dispatch }) => {
    try {
      const filters = await syncFiltersFromDatabase();
      dispatch(setFilters(filters));
      dispatch(showGlobalMessage({
        message: '🔄 Filters synchronized from database!',
        type: 'success'
      }));
      console.log('🔄 Synchronized filters from database');
      return filters;
    } catch (error: any) {
      console.error('Error syncing filters from database:', error);
      dispatch(showGlobalMessage({
        message: `❌ ${error.message || 'Error syncing filters from database'}`,
        type: 'danger'
      }));
      throw error;
    }
  }
);


// Screening
export const screenStocksThunk = createAsyncThunk(
  'screening/start',
  async (options: { finviz_tickers?: string[] } | void, { dispatch, getState }) => {
    const state = getState() as any;
    const currentStatus = state.screening?.status;
    if (screeningStartInFlight || currentStatus === 'running' || currentStatus === 'loading') {
      dispatch(showGlobalMessage({
        message: 'ℹ️ Screening is already running.',
        type: 'info'
      }));
      return;
    }

    screeningStartInFlight = true;
    try {
      // Get current state
      const filters = state.filters;

      // Backend can already be running (e.g. page refresh, another tab/session).
      // Sync first to avoid sending a duplicate POST that produces an expected 409.
      try {
        const liveStatus = await pollScreeningStatus();
        if (liveStatus?.status === 'running') {
          // Clear table like a fresh run; do not call setScreeningResults([]) here - it bumps
          // last_updated and could trigger chart fetches before status is "running".
          invalidateResultsCache();
          dispatch(setStocks([]));
          dispatch(clearChart());
          dispatch(setScreeningStatus({
            status: liveStatus.status,
            current_stock: liveStatus.current_stock,
            processed_count: liveStatus.processed_count,
            total_stocks: liveStatus.total_stocks,
            passing_count: liveStatus.passing_count,
            failed_count: liveStatus.failed_count,
            error_count: liveStatus.error_count,
            message: liveStatus.message || 'Screening already running',
            results: []
          }));
          dispatch(showGlobalMessage({
            message: 'ℹ️ Screening is already in progress. Showing live status.',
            type: 'info'
          }));
          return;
        }
      } catch (statusSyncError) {
        // Non-fatal: continue and try to start screening normally.
        console.warn('Could not pre-check live screening status:', statusSyncError);
      }

      // CRITICAL FIX: Clear old results before starting new screening
      // This prevents showing stale data from previous runs
      invalidateResultsCache();
      dispatch(setScreeningResults([]));
      dispatch(setStocks([]));
      dispatch(clearChart()); // Close chart preview when starting new screening
      console.log('🧹 Cleared previous screening results and chart preview');

      // Set initial running state
      dispatch(setScreeningStatus({
        status: 'running',
        current_stock: '',
        processed_count: 0,
        total_stocks: 0,
        passing_count: 0,
        failed_count: 0,
        error_count: 0,
        message: 'Starting screening...',
        results: []
      }));

      // Finviz tickers are now auto-loaded from DB cache by the backend.
      // No separate finviz-only flow needed - /screen_stocks will use cached
      // Finviz tickers as the stock universe and run custom filters on them.

      // Start the screening process with current filter configuration
      const hasExplicitFinvizTickers = !!(options?.finviz_tickers && options.finviz_tickers.length > 0);
      const screeningOptions: Parameters<typeof screenStocks>[0] = {
        filter_category: filters.active_category || 'custom',
        min_score: filters.min_composite_score || 500,
        include_entry_analysis: true,
        sh_avgvol: (filters as any).sh_avgvol || '',
        sh_relvol: (filters as any).sh_relvol || '',
        sh_curvol: (filters as any).sh_curvol || '',
        // Only use finviz cache when explicitly triggered from Finviz dialog with tickers,
        // never from persisted DB state - otherwise normal screening gets restricted
        use_finviz_cache: hasExplicitFinvizTickers ? true : false,
      };
      // Pass direct finviz tickers if provided (from Finviz Screener dialog)
      if (hasExplicitFinvizTickers) {
        screeningOptions.finviz_tickers = options!.finviz_tickers!;
      }
      await screenStocks(screeningOptions);

      // Optimized polling with smart throttling and diff detection
      let lastKnownStatus: any = null;
      const pollStatus = async () => {
        try {
          // Use smart throttling for polling frequency
          if (!screeningPollingThrottle.shouldUpdate()) {
            // Continue polling even if throttled
            if (lastKnownStatus?.status === 'running') {
              setTimeout(pollStatus, 500);
            }
            return;
          }

          const status = await performanceMonitor.measureAsync(
            'Poll Screening Status',
            () => pollScreeningStatus()
          );

          // Only update if status actually changed
          if (!lastKnownStatus || !areScreeningStatusesEqual(lastKnownStatus, status)) {
            // For running status, don't include results in the update to preserve existing results
            const statusUpdate = status.status === 'running'
              ? {
                  status: status.status,
                  current_stock: status.current_stock,
                  processed_count: status.processed_count,
                  total_stocks: status.total_stocks,
                  passing_count: status.passing_count,
                  failed_count: status.failed_count,
                  error_count: status.error_count,
                  message: status.message || 'Screening stocks...'
                }
              : {
                  status: status.status,
                  current_stock: status.current_stock,
                  processed_count: status.processed_count,
                  total_stocks: status.total_stocks,
                  passing_count: status.passing_count,
                  failed_count: status.failed_count,
                  error_count: status.error_count,
                  // Preserve existing results while transitioning to completed/stopped.
                  // Final results are reconciled below via getResults(true).
                  message: status.message || 'Screening stocks...'
                };

            dispatch(setScreeningStatus(statusUpdate));

            lastKnownStatus = status;

            // Reset throttle on status change for more responsive updates
            screeningPollingThrottle.setActive();
          }

          // Continue polling if still running
          if (status.status === 'running') {
            // Fetch incremental results during screening for real-time updates
            try {
              const runningResults = await performanceMonitor.measureAsync(
                'Fetch Running Results',
                // Live polling must hit the backend; cached results can belong to the previous run.
                () => getResults(true)
              );
              if (runningResults && runningResults.length > 0) {
                const currentState = getState() as any;
                const currentResults = currentState.screening.results;
                // Only update if results changed
                if (!areScreeningResultsEqual(currentResults, runningResults)) {
                  dispatch(setScreeningResults(runningResults));
                  dispatch(setStocks(runningResults));
                  console.log(`📊 Real-time update: ${runningResults.length} stocks found so far`);
                }
              }
            } catch (error) {
              // Silently ignore - results will be fetched on completion
            }
            setTimeout(pollStatus, 1000); // Poll every second for real-time updates
          } else if (status.status === 'completed' || (status.status as string) === 'stopped') {
            const logIcon = (status.status as string) === 'stopped' ? '⚠️' : '🎯';
            const logMessage = (status.status as string) === 'stopped' ? 'Screening stopped' : 'Screening completed';
            console.log(`${logIcon} ${logMessage}! Fetching full results...`);

            // Reset polling throttle on completion
            screeningPollingThrottle.reset();

            // Fetch the full results from the dedicated results endpoint (skip cache to get fresh data)
            try {
              const results = await performanceMonitor.measureAsync(
                'Fetch Screening Results',
                () => getResults(true)  // Skip cache after screening completes
              );

              console.log('📊 Got results from API:', {
                count: results?.length || 0,
                firstResult: results?.[0]?.ticker || 'None',
                timestamp: new Date().toISOString()
              });

              // Backend invariant I2 (persist-before-flip) + I3b (owner in-memory
              // fallback when DB save returned None) guarantees that a completion
              // status flip implies an authoritative result for the run owner.
              // The legitimate-empty run (T8) must overwrite stale prior results,
              // so we no longer keep-stale on empty.
              const currentState = getState() as any;
              const currentResults = currentState.screening.results;

              if (!areScreeningResultsEqual(currentResults, results)) {
                dispatch(setScreeningResults(results));
                dispatch(setStocks(results));
                dispatch(setLastUpdated(new Date().toISOString()));
                console.log(`🔄 Dispatched setStocks and setScreeningResults (count=${results?.length ?? 0})`);
              }

              // Refresh favorites and show success message in all cases
              dispatch(fetchFavoritesThunk());

              // Show appropriate message based on completion status
              const messageType = (status.status as string) === 'stopped' ? 'warning' : 'success';
              const messageIcon = (status.status as string) === 'stopped' ? '⚠️' : '✅';
              const messageText = (status.status as string) === 'stopped'
                ? `${messageIcon} Screening stopped by user. Found ${status.passing_count} stocks before stopping. Favorites refreshed.`
                : `${messageIcon} Screening completed! Found ${status.passing_count} qualifying stocks. Favorites refreshed.`;

              dispatch(showGlobalMessage({
                message: messageText,
                type: messageType
              }));
            } catch (error) {
              console.error('❌ Error fetching results:', error);
              dispatch(showGlobalMessage({
                message: '❌ Error loading screening results',
                type: 'danger'
              }));
            }
          } else if (status.status === 'error') {
            // Reset throttle on error
            screeningPollingThrottle.reset();

            dispatch(showGlobalMessage({
              message: '❌ Screening failed',
              type: 'danger'
            }));
          }
        } catch (error) {
          console.error('Error polling screening status:', error);
          // Reset throttle on error
          screeningPollingThrottle.reset();
          dispatch(setScreeningStatus({ status: 'error' }));
        }
      };

      // Start polling after a short delay
      setTimeout(pollStatus, 1000);

    } catch (error: any) {
      const errorMessage = error?.message || 'Error starting screening';
      const alreadyRunning = /already in progress/i.test(errorMessage);

      if (alreadyRunning) {
        console.warn('Screening start skipped: backend already in progress');
        // Keep UI in sync with backend lock instead of showing a false "error" state.
        dispatch(setScreeningStatus({
          status: 'running',
          message: 'Screening already in progress. Syncing current status...'
        }));
        dispatch(showGlobalMessage({
          message: 'ℹ️ Screening is already in progress. Please wait for it to finish or stop it.',
          type: 'info'
        }));

        try {
          const status = await pollScreeningStatus();
          dispatch(setScreeningStatus({
            status: status.status,
            current_stock: status.current_stock,
            processed_count: status.processed_count,
            total_stocks: status.total_stocks,
            passing_count: status.passing_count,
            failed_count: status.failed_count,
            error_count: status.error_count,
            message: status.message || 'Screening in progress',
            results: []
          }));
        } catch (syncError) {
          console.warn('Could not sync screening status after 409:', syncError);
        }
        return;
      }

      console.error('Error starting screening:', error);
      dispatch(setScreeningStatus({ status: 'error' }));

      // Show a user-friendly message for daily scan limits
      const isDailyLimit = error?.status === 403 || /daily.*limit|scan limit/i.test(errorMessage);
      dispatch(showGlobalMessage({
        message: isDailyLimit
          ? `⚠️ ${errorMessage}`
          : `❌ ${errorMessage}`,
        type: isDailyLimit ? 'warning' : 'danger'
      }));
      throw error;
    } finally {
      screeningStartInFlight = false;
    }
  }
);

// Stop screening
export const stopScreeningThunk = createAsyncThunk(
  'screening/stop',
  async (_, { dispatch }) => {
    try {
      const response = await stopScreeningAPI();

      if (response.success) {
        // Update screening status to stopped
        dispatch(setScreeningStatus({
          status: 'idle',
          message: 'Screening stopped by user'
        }));

        dispatch(showGlobalMessage({
          message: 'Screening stopped by user',
          type: 'warning'
        }));

        console.log('Screening stopped successfully');
        return response;
      } else {
        throw new Error(response.message || 'Failed to stop screening');
      }
    } catch (error: any) {
      console.error('Error stopping screening:', error);
      dispatch(showGlobalMessage({
        message: `Failed to stop screening: ${error.message}`,
        type: 'danger'
      }));
      throw error;
    }
  }
);

// Chart data with cancellation support
export const fetchChartData = createAsyncThunk<ChartData, { ticker: string; timeframe?: string }>(
  'chart/fetchData',
  async ({ ticker, timeframe = 'DAILY' }, { dispatch, signal }) => {
    // Cancel any existing chart data requests
    const cacheKey = createRequestCacheKey(
      'GET',
      `/chart_data/${ticker}?timeframe=${encodeURIComponent(timeframe)}`
    );
    cancelRequest(cacheKey);

    try {
      dispatch(setChartLoading(true));
      const data = await getChartData(ticker, timeframe, signal);

      // Check if request was cancelled
      if (signal.aborted) {
        throw new Error('Request was cancelled');
      }

      dispatch(setChartData(data));
      return data;
    } catch (error: any) {
      // Don't show error for cancelled requests
      if (error.message !== 'Request was cancelled' && !signal.aborted) {
        console.error('Error fetching chart data:', error);
        dispatch(setChartError('Error loading chart data'));
      }
      throw error;
    }
  }
);

export const refreshChartData = createAsyncThunk<ChartData, { ticker: string; timeframe?: string }>(
  'chart/refreshData',
  async ({ ticker, timeframe = 'DAILY' }, { dispatch, signal }) => {
    // Cancel any existing requests for this ticker
    const cacheKey = createRequestCacheKey(
      'GET',
      `/chart_data/${ticker}?timeframe=${encodeURIComponent(timeframe)}`
    );
    cancelRequest(cacheKey);

    try {
      console.log(`🔄 Force refreshing chart data for ${ticker} (bypassing cache)`);
      dispatch(setChartLoading(true));
      const { refreshChartData: refreshChartDataAPI } = await import('../../services/api');
      const data = await refreshChartDataAPI(ticker, timeframe);

      // Check if request was cancelled
      if (signal.aborted) {
        throw new Error('Request was cancelled');
      }

      dispatch(setChartData(data));
      console.log(`✅ Chart data refreshed successfully for ${ticker}`);
      return data;
    } catch (error: any) {
      // Don't show error for cancelled requests
      if (error.message !== 'Request was cancelled' && !signal.aborted) {
        console.error('Error refreshing chart data:', error);
        dispatch(setChartError('Error refreshing chart data'));
      }
      throw error;
    }
  }
);

// AI Analysis
export const analyzeStocksThunk = createAsyncThunk(
  'aiAnalysis/analyze',
  async ({ tickers, analysisType }: { tickers: string[]; analysisType?: string }, { dispatch, signal }) => {
    // Cancel any pending analysis requests
    cancelAnalysisRequests();

    try {
      dispatch(setAIAnalysisLoading(true));
      const data = await analyzeStocks(tickers, analysisType, signal);

      // Check if request was cancelled
      if (signal.aborted) {
        throw new Error('Request was cancelled');
      }

      dispatch(setAIAnalysisData(data));
      return data;
    } catch (error: any) {
      // Don't show error for cancelled requests
      if (error.message !== 'Request was cancelled' && !signal.aborted) {
        console.error('Error analyzing stocks:', error);
        dispatch(setAIAnalysisError('Error analyzing stocks'));
      }
      throw error;
    }
  }
);

export const compareStocksThunk = createAsyncThunk(
  'aiAnalysis/compare',
  async (tickers: string[], { dispatch }) => {
    try {
      dispatch(setAIAnalysisLoading(true));
      const data = await compareStocks(tickers);
      dispatch(setAIAnalysisData(data));
      return data;
    } catch (error) {
      console.error('Error comparing stocks:', error);
      dispatch(setAIAnalysisError('Error comparing stocks'));
      throw error;
    }
  }
);

export const analyzeStockColorThunk = createAsyncThunk(
  'aiAnalysis/analyzeColor',
  async (tickers: string[], { dispatch }) => {
    try {
      dispatch(setAIAnalysisLoading(true));
      const data = await analyzeStockColor(tickers);
      dispatch(setColorAnalysisData(data));
      return data;
    } catch (error) {
      console.error('Error analyzing stock colors:', error);
      dispatch(setAIAnalysisError('Error analyzing stock colors'));
      throw error;
    }
  }
);

export const analyzeStockWithImageThunk = createAsyncThunk(
  'aiAnalysis/analyzeWithImage',
  async ({ ticker, imageFile }: { ticker: string; imageFile: File }, { dispatch }) => {
    try {
      dispatch(setAIAnalysisLoading(true));

      // Use the API service function
      const data = await analyzeStockWithImage(ticker, imageFile);

      dispatch(setImageAnalysisData(data));
      dispatch(showGlobalMessage({
        message: `✅ Image analysis completed for ${ticker}!`,
        type: 'success'
      }));
      return data;
    } catch (error: any) {
      console.error('Error analyzing stock with image:', error);
      const errorMessage = error.message || 'Error analyzing stock with image';
      dispatch(setAIAnalysisError(errorMessage));
      dispatch(showGlobalMessage({
        message: `❌ ${errorMessage}`,
        type: 'danger'
      }));
      throw error;
    }
  }
);

export const analyzeStockStructuredThunk = createAsyncThunk(
  'aiAnalysis/analyzeStructured',
  async (ticker: string, { dispatch }) => {
    try {
      dispatch(setAIAnalysisLoading(true));

      // Use the API service function for structured text analysis
      const data = await analyzeStockStructured(ticker);

      dispatch(setImageAnalysisData(data));
      dispatch(showGlobalMessage({
        message: `✅ Structured analysis completed for ${ticker}!`,
        type: 'success'
      }));
      return data;
    } catch (error: any) {
      console.error('Error analyzing stock (structured):', error);
      const errorMessage = error.message || 'Error analyzing stock';
      dispatch(setAIAnalysisError(errorMessage));
      dispatch(showGlobalMessage({
        message: `❌ ${errorMessage}`,
        type: 'danger'
      }));
      throw error;
    }
  }
);

// Favorites
export const fetchFavoritesThunk = createAsyncThunk(
  'favorites/fetch',
  async (_, { dispatch, signal }) => {
    try {
      dispatch(setLoading(true));
      const favorites = await getFavorites(signal);
      dispatch(setFavorites(favorites));
      return favorites;
    } catch (error: any) {
      console.error('Error fetching favorites:', error);
      dispatch(setError(error.message || 'Error fetching favorites'));
      throw error;
    }
  }
);

export const addFavoriteThunk = createAsyncThunk(
  'favorites/add',
  async ({ ticker, color, notes }: { ticker: string; color: string; notes?: string }, { dispatch, getState }) => {
    try {
      dispatch(setLoading(true));

      // Check if already exists in local state first
      const state = getState() as any;
      const existingFavorite = state.favorites?.favorites?.find((fav: any) => fav.ticker === ticker.toUpperCase());

      if (existingFavorite) {
        dispatch(setLoading(false));
        dispatch(showGlobalMessage({
          message: `ℹ️ ${ticker} is already in your favorites!`,
          type: 'info'
        }));
        return existingFavorite;
      }

      const favorite = await addFavoriteAPI(ticker, color, notes || '');
      dispatch(addFavorite(favorite));
      dispatch(showGlobalMessage({
        message: `✅ ${ticker} added to favorites!`,
        type: 'success'
      }));
      return favorite;
    } catch (error: any) {
      console.error('Error adding favorite:', error);
      dispatch(setError(error.message || 'Error adding favorite'));

      // Handle specific 409 conflict error
      if (error.message && error.message.includes('already in favorites')) {
        dispatch(showGlobalMessage({
          message: `ℹ️ ${ticker} is already in your favorites!`,
          type: 'info'
        }));
        // Refresh favorites to sync state
        dispatch(fetchFavoritesThunk());
      } else {
        dispatch(showGlobalMessage({
          message: `❌ Error adding ${ticker} to favorites: ${error.message}`,
          type: 'danger'
        }));
      }
      throw error;
    }
  }
);

export const updateFavoriteThunk = createAsyncThunk(
  'favorites/update',
  async ({ ticker, color, notes }: { ticker: string; color?: string; notes?: string }, { dispatch }) => {
    try {
      dispatch(setLoading(true));
      const favorite = await updateFavoriteAPI(ticker, color, notes);
      dispatch(updateFavorite(favorite));
      dispatch(showGlobalMessage({
        message: `✅ ${ticker} favorite updated!`,
        type: 'success'
      }));
      return favorite;
    } catch (error: any) {
      console.error('Error updating favorite:', error);
      dispatch(setError(error.message || 'Error updating favorite'));
      dispatch(showGlobalMessage({
        message: `❌ Error updating ${ticker}: ${error.message}`,
        type: 'danger'
      }));
      throw error;
    }
  }
);

export const removeFavoriteThunk = createAsyncThunk(
  'favorites/remove',
  async (ticker: string, { dispatch }) => {
    try {
      dispatch(setLoading(true));
      await deleteFavoriteAPI(ticker);
      dispatch(removeFavorite(ticker));
      dispatch(showGlobalMessage({
        message: `✅ ${ticker} removed from favorites!`,
        type: 'success'
      }));
      return ticker;
    } catch (error: any) {
      console.error('Error removing favorite:', error);
      dispatch(setError(error.message || 'Error removing favorite'));
      dispatch(showGlobalMessage({
        message: `❌ Error removing ${ticker}: ${error.message}`,
        type: 'danger'
      }));
      throw error;
    }
  }
);

// Force Data Update
export const forceDataUpdateThunk = createAsyncThunk(
  'database/forceUpdate',
  async (ignoreMarketHours: boolean = false, { dispatch }) => {
    try {
      dispatch(showGlobalMessage({
        message: '🔄 Starting data update...',
        type: 'info'
      }));

      const result = await forceDataUpdate(ignoreMarketHours);

      if (result.success) {
        dispatch(showGlobalMessage({
          message: `✅ ${result.message}`,
          type: 'success'
        }));

        // Refresh database stats after successful update
        dispatch(fetchDatabaseStats(false));
      } else {
        throw new Error(result.message);
      }

      return result;
    } catch (error: any) {
      console.error('Error forcing data update:', error);
      const errorMessage = error.message || 'Error updating data';
      dispatch(showGlobalMessage({
        message: `❌ ${errorMessage}`,
        type: 'danger'
      }));
      throw error;
    }
  }
);

// Grok Sentiment Analysis
export const fetchGrokSentiment = createAsyncThunk(
  'grokSentiment/fetch',
  async ({ ticker, forceRefresh = false }: { ticker: string; forceRefresh?: boolean }, { dispatch, signal }) => {
    try {
      dispatch(setSentimentLoading(true));
      const result = await analyzeTickerWithGrok(ticker, signal, forceRefresh);

      if (signal.aborted) {
        throw new Error('Request was cancelled');
      }

      if (result.success) {
        dispatch(setSentimentData(result as any));
      } else {
        dispatch(setSentimentError(result.error || 'Failed to fetch sentiment'));
      }

      return result;
    } catch (error: any) {
      if (error.message !== 'Request was cancelled' && !signal.aborted) {
        dispatch(setSentimentError(`Error fetching sentiment: ${error.message}`));
      }
      throw error;
    }
  }
);

export const fetchGrokSentimentBatch = createAsyncThunk(
  'grokSentiment/fetchBatch',
  async ({ tickers, forceRefresh = false }: { tickers: string[]; forceRefresh?: boolean }, { dispatch, signal }) => {
    try {
      dispatch(setBatchSentimentLoading(true));
      const result = await analyzeBatchWithGrok(tickers, signal, forceRefresh);

      if (signal.aborted) {
        throw new Error('Request was cancelled');
      }

      if (result.success) {
        dispatch(setBatchSentimentData(result.results));
      } else {
        dispatch(setSentimentError(result.error || 'Failed to fetch batch sentiment'));
      }

      return result;
    } catch (error: any) {
      if (error.message !== 'Request was cancelled' && !signal.aborted) {
        dispatch(setSentimentError(`Error fetching batch sentiment: ${error.message}`));
      }
      throw error;
    }
  }
);

// Perplexity Stock Analysis
export const fetchPerplexityStockAnalysis = createAsyncThunk(
  'perplexityAnalysis/fetch',
  async (
    { ticker, includeChartContext, forceRefresh }: { ticker: string; includeChartContext?: boolean; forceRefresh?: boolean },
    { dispatch, signal }
  ) => {
    try {
      dispatch(setAnalysisLoading(true));
      const result = await analyzeStockWithPerplexity(ticker, includeChartContext || false, signal, forceRefresh || false);

      if (signal.aborted) {
        throw new Error('Request was cancelled');
      }

      if (result.success) {
        dispatch(setAnalysisData(result as any));
      } else {
        dispatch(setAnalysisError(result.error || 'Failed to fetch analysis'));
      }

      return result;
    } catch (error: any) {
      if (error.message !== 'Request was cancelled' && !signal.aborted) {
        dispatch(setAnalysisError(`Error fetching analysis: ${error.message}`));
      }
      throw error;
    }
  }
);

export const fetchPerplexityStockAnalysisWithChart = createAsyncThunk(
  'perplexityAnalysis/fetchWithChart',
  async (
    { ticker, chartImageFile }: { ticker: string; chartImageFile: File },
    { dispatch, signal }
  ) => {
    try {
      dispatch(setAnalysisLoading(true));
      const result = await analyzeStockWithPerplexityChart(ticker, chartImageFile, signal);

      if (signal.aborted) {
        throw new Error('Request was cancelled');
      }

      if (result.success) {
        dispatch(setAnalysisData(result as any));
        return result;
      } else {
        const errorMsg = result.error || 'Failed to fetch analysis with chart';
        dispatch(setAnalysisError(errorMsg));
        throw new Error(errorMsg);
      }
    } catch (error: any) {
      if (error.message !== 'Request was cancelled' && !signal.aborted) {
        dispatch(setAnalysisError(`Error fetching chart analysis: ${error.message}`));
      }
      throw error;
    }
  }
);
